import type { ReactElement } from 'react';
import * as Sentry from '@sentry/react';

// Initialize pdfjs only when needed
export const initializePdfjs = async () => {
  const { pdfjs } = await import('react-pdf');
  pdfjs.GlobalWorkerOptions.workerSrc =
    'https://firebasestorage.googleapis.com/v0/b/bamboo-artwork-372213.appspot.com/o/public%2Fnew.pdf.worker.min.mjs?alt=media';
};

export const mergePDFs = async (pdfBuffers: ArrayBuffer[]) => {
  const { PDFDocument } = await import('pdf-lib');
  const { Buffer } = await import('buffer');

  const mergedPdf = await PDFDocument.create();
  // eslint-disable-next-line no-restricted-syntax
  for (const pdfBytes of pdfBuffers) {
    const pdf = await PDFDocument.load(pdfBytes);
    const copiedPages = await mergedPdf.copyPages(pdf, pdf.getPageIndices());
    copiedPages.forEach(page => {
      mergedPdf.addPage(page);
    });
  }

  const buf = await mergedPdf.save();
  return Buffer.from(buf);
};

// @react-pdf/renderer has a caching bug that can under-measure a text
// block's height, so this checks the rendered output for overlapping text.
async function hasOverlappingText(pdfBuffer: ArrayBuffer): Promise<boolean> {
  const { pdfjs } = await import('react-pdf');
  await initializePdfjs();
  // pdf.js transfers the buffer to its worker, detaching it for the caller,
  // so give it a copy — the original is still needed for merging.
  const doc = await pdfjs.getDocument({ data: pdfBuffer.slice(0) }).promise;
  try {
    for (let pageNum = 1; pageNum <= doc.numPages; pageNum += 1) {
      // eslint-disable-next-line no-await-in-loop
      const page = await doc.getPage(pageNum);
      // eslint-disable-next-line no-await-in-loop
      const content = await page.getTextContent();
      const items = (
        content.items as Array<{
          str?: string;
          transform?: number[];
          width?: number;
          height?: number;
        }>
      )
        .filter(item => !!item.str && item.str.trim().length > 0)
        .map(item => ({
          str: item.str!,
          x0: item.transform![4],
          y0: item.transform![5],
          x1: item.transform![4] + item.width!,
          y1: item.transform![5] + item.height!,
        }));

      for (let i = 0; i < items.length; i += 1) {
        for (let j = i + 1; j < items.length; j += 1) {
          const a = items[i];
          const b = items[j];
          const overlaps =
            a.str !== b.str &&
            a.x0 < b.x1 - 1 &&
            a.x1 > b.x0 + 1 &&
            a.y0 < b.y1 - 1 &&
            a.y1 > b.y0 + 1;
          if (overlaps) return true;
        }
      }
    }
    return false;
  } finally {
    // Teardown failures must not mask the detection result above.
    await doc.destroy().catch(() => {});
  }
}

const MAX_RENDER_ATTEMPTS = 3;

export async function renderPDFSafely(
  buildElement: () => ReactElement,
  context: Record<string, unknown> = {},
): Promise<ArrayBuffer> {
  const { pdf } = await import('@react-pdf/renderer');
  let lastBuffer: ArrayBuffer | null = null;

  for (let attempt = 1; attempt <= MAX_RENDER_ATTEMPTS; attempt += 1) {
    const instance = pdf(buildElement());
    // eslint-disable-next-line no-await-in-loop
    const blob = await instance.toBlob();
    // eslint-disable-next-line no-await-in-loop
    const buffer = await blob.arrayBuffer();
    lastBuffer = buffer;

    // eslint-disable-next-line no-await-in-loop
    const overlapping = await hasOverlappingText(buffer).catch(error => {
      Sentry.withScope(scope => {
        scope.setTag('feature', 'waiver_pdf_render');
        scope.setLevel('warning');
        scope.setContext('pdfRender', {
          ...context,
          attempt,
          maxAttempts: MAX_RENDER_ATTEMPTS,
        });
        Sentry.captureException(error);
      });
      return false;
    });
    if (!overlapping) return buffer;

    // Report only when every attempt failed — a render that recovers on a
    // retry is working as designed and shouldn't emit events.
    if (attempt === MAX_RENDER_ATTEMPTS) {
      Sentry.withScope(scope => {
        scope.setTag('feature', 'waiver_pdf_render');
        scope.setLevel('error');
        scope.setContext('pdfRender', {
          ...context,
          attempt,
          maxAttempts: MAX_RENDER_ATTEMPTS,
        });
        Sentry.captureException(
          new Error('Waiver PDF render produced overlapping text'),
        );
      });
    }
  }

  return lastBuffer!;
}
